import { physicsWorld } from './PhysicsWorld';
import {
  setPreviewModePhysicsEnabled,
  setPreviewModePhysicsWorld,
  addPreviewModePhysicsObject,
  removePreviewModePhysicsObject,
  clearPreviewModePhysics,
  setObjectPhysicsProperties,
} from '../../redux/Slice/physicsSlice';
import store from '../../redux/store';
import { findPhysicsDataForObject } from './physicsObjectMatch';

/**
 * Physics Utilities
 * Helper functions for managing physics in the application
 */

/**
 * Initialize physics for preview mode
 */
export const initializePreviewPhysics = () => {
  const dispatch = store.dispatch;

  // Initialize physics world if not already done
  if (!physicsWorld.world) {
    physicsWorld.init();
  }

  // Enable physics world
  physicsWorld.setEnabled(true);

  // Update Redux state
  dispatch(setPreviewModePhysicsEnabled(true));
  dispatch(setPreviewModePhysicsWorld(physicsWorld));
};

/**
 * Disable physics for preview mode. Scene transforms are restored by
 * sceneStateHelper when leaving preview — not from physics initialStates.
 */
export const disablePreviewPhysics = () => {
  const dispatch = store.dispatch;

  physicsWorld.setEnabled(false);

  dispatch(setPreviewModePhysicsEnabled(false));
  dispatch(clearPreviewModePhysics());
};

/**
 * Rebuild physics bodies from current mesh transforms during preview replay.
 */
export const replayPreviewPhysics = (scene) => {
  if (!scene) return;

  const state = store.getState();
  if (!state.physics.previewModePhysics.enabled) return;

  const physicsObjects = state.physics.physicsObjects || [];
  if (!physicsObjects.length) return;

  physicsWorld.resetBodiesForReplay();

  physicsObjects.forEach((physicsObject) => {
    if (physicsObject.enabled === false) return;

    const { objectId } = physicsObject;
    let mesh = scene.getObjectByName(objectId);

    if (!mesh) {
      scene.traverse((child) => {
        if (
          !mesh &&
          (child.name === objectId ||
            child._id === objectId ||
            child.url === objectId ||
            child.userData?.id === objectId ||
            child.userData?._id === objectId ||
            child.userData?.temp_file_name === objectId)
        ) {
          mesh = child;
        }
      });
    }

    if (mesh) {
      applyPhysicsToObject(objectId, mesh, physicsObject);
    }
  });
};

/**
 * Apply physics to a specific object
 */
export const applyPhysicsToObject = (objectId, mesh, physicsProperties) => {
  if (!objectId || !mesh) {
    console.warn('PhysicsUtils: Invalid parameters for applyPhysicsToObject', {
      objectId: !!objectId,
      mesh: !!mesh,
      physicsProperties: !!physicsProperties,
    });
    return null;
  }

  // If physicsProperties is missing, attempt to infer sensible defaults
  if (!physicsProperties) {
    const inferred = {};

    // Helper detections
    const looksLikeImage =
      mesh.type === 'Sprite' ||
      !!(
        mesh.material &&
        mesh.material.map &&
        mesh.material.map.image &&
        mesh.material.map.image.tagName === 'IMG'
      );
    const looksLikeVideo =
      !!(
        mesh.material &&
        mesh.material.map &&
        mesh.material.map.image &&
        mesh.material.map.image.tagName === 'VIDEO'
      ) || mesh.userData?.assetType === 'video';
    const isAudio = !!(
      mesh.userData &&
      (mesh.userData.assetType === 'audio' ||
        mesh.userData.isAudio ||
        /audio/i.test(mesh.name || ''))
    );
    const hasGeometry = !!mesh.geometry;

    if (looksLikeImage || looksLikeVideo || isAudio) {
      // Media (images/videos/audio) are often flat, transformable objects.
      // Use a finite thin Box collider that matches the visual bounds so
      // physics (collisions, gravity) behave predictably. Make it dynamic
      // by default so media objects will fall under gravity unless caller
      // overrides the mass.
      inferred.colliderType = 'Box';
      inferred.mass = 1; // dynamic by default so media fall
      inferred.physicsMaterial = 'Default';
    } else if (hasGeometry) {
      // For mesh objects, use a sphere collider for simpler, stable collisions
      inferred.colliderType = 'Sphere';
      inferred.mass = 1;
      inferred.physicsMaterial = 'Default';
    } else {
      // Fallback
      inferred.colliderType = 'Plane';
      inferred.mass = 0;
      inferred.physicsMaterial = 'Default';
    }

    inferred.useGravity = true;
    inferred.isKinematic = false;
    inferred.linearDamping = 0.01;
    inferred.angularDamping = 0.01;

    physicsProperties = inferred;
  }

  try {
    // Create physics body
    const physicsBody = physicsWorld.createPhysicsBody(
      objectId,
      mesh,
      physicsProperties
    );

    if (physicsBody) {
      // Update Redux state
      const dispatch = store.dispatch;
      dispatch(addPreviewModePhysicsObject({ objectId, physicsBody }));

      
      return physicsBody;
    } else {
      console.error('PhysicsUtils: Failed to create physics body', {
        objectId,
      });
    }
  } catch (error) {
    console.error('PhysicsUtils: Error applying physics to object:', error);
  }

  return null;
};

/**
 * Remove physics from a specific object
 */
export const removePhysicsFromObject = (objectId) => {
  if (!objectId) {
    console.warn('Invalid objectId for removePhysicsFromObject');
    return;
  }

  try {
    // Remove physics body
    physicsWorld.removePhysicsBody(objectId);

    // Update Redux state
    const dispatch = store.dispatch;
    dispatch(removePreviewModePhysicsObject(objectId));

    
  } catch (error) {
    console.error('Error removing physics from object:', error);
  }
};

/**
 * Update physics properties for an existing object
 */
export const updateObjectPhysics = (objectId, physicsProperties) => {
  if (!objectId || !physicsProperties) {
    console.warn('Invalid parameters for updateObjectPhysics');
    return;
  }

  try {
    // Update physics body
    physicsWorld.updatePhysicsBody(objectId, physicsProperties);

    
  } catch (error) {
    console.error('Error updating object physics:', error);
  }
};

/**
 * Sync all physics objects with their meshes
 */
export const syncPhysicsWithMeshes = () => {
  physicsWorld.syncBodiesWithMeshes();
};

/**
 * Update physics world (call in animation loop)
 */
export const updatePhysicsWorld = (deltaTime) => {
  physicsWorld.update(deltaTime);
};

/**
 * Get physics body for an object
 */
export const getPhysicsBody = (objectId) => {
  return physicsWorld.getPhysicsBody(objectId);
};

/**
 * Check if object has physics
 */
export const hasPhysics = (objectId) => {
  return physicsWorld.hasPhysics(objectId);
};

/**
 * Set world gravity
 */
export const setWorldGravity = (x, y, z) => {
  physicsWorld.setGravity(x, y, z);
  const g = physicsWorld.getGravity();
  try {
    // Always use 
    
  } catch (e) {}
  return g;
};

/**
 * Get current world gravity vector
 */
export const getWorldGravity = () => {
  try {
    const g = physicsWorld.getGravity();
    return g;
  } catch (e) {
    return { x: 0, y: -9.82, z: 0 };
  }
};

/**
 * Get all physics objects
 */
export const getAllPhysicsObjects = () => {
  const state = store.getState();
  return state.physics.previewModePhysics.objects;
};

/**
 * Clear all physics
 */
export const clearAllPhysics = () => {
  physicsWorld.clearAllBodies();

  const dispatch = store.dispatch;
  dispatch(clearPreviewModePhysics());

  
};

/**
 * Create physics body from mesh geometry
 */
export const createPhysicsBodyFromMesh = (mesh, physicsProperties) => {
  if (!mesh || !mesh.geometry) {
    console.warn('Invalid mesh for createPhysicsBodyFromMesh');
    return null;
  }

  try {
    const shape = physicsWorld.createShape(
      mesh,
      physicsProperties.colliderType
    );
    if (!shape) return null;

    const material = physicsWorld.getPhysicsMaterial(
      physicsProperties.physicsMaterial
    );

    const body = new (require('cannon-es').Body)({
      mass: physicsProperties.mass,
      material: material,
      shape: shape,
    });

    // Set properties
    body.linearDamping = physicsProperties.linearDamping;
    body.angularDamping = physicsProperties.angularDamping;
    body.position.copy(mesh.position);
    body.quaternion.copy(mesh.quaternion);
    body.type = physicsProperties.isKinematic
      ? require('cannon-es').Body.KINEMATIC
      : require('cannon-es').Body.DYNAMIC;

    return body;
  } catch (error) {
    console.error('Error creating physics body from mesh:', error);
    return null;
  }
};

/**
 * Apply force to physics body
 */
export const applyForceToObject = (objectId, force, point) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    const CANNON = require('cannon-es');
    const forceVec = new CANNON.Vec3(force.x, force.y, force.z);
    const pointVec = point
      ? new CANNON.Vec3(point.x, point.y, point.z)
      : body.position;

    body.applyForce(forceVec, pointVec);
    
  }
};

/**
 * Apply impulse to physics body
 */
export const applyImpulseToObject = (objectId, impulse, point) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    const CANNON = require('cannon-es');
    const impulseVec = new CANNON.Vec3(impulse.x, impulse.y, impulse.z);
    const pointVec = point
      ? new CANNON.Vec3(point.x, point.y, point.z)
      : body.position;

    body.applyImpulse(impulseVec, pointVec);
    
  }
};

/**
 * Set object velocity
 */
export const setObjectVelocity = (objectId, velocity) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    const CANNON = require('cannon-es');
    body.velocity.set(velocity.x, velocity.y, velocity.z);
    
  }
};

/**
 * Set object angular velocity
 */
export const setObjectAngularVelocity = (objectId, angularVelocity) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    const CANNON = require('cannon-es');
    body.angularVelocity.set(
      angularVelocity.x,
      angularVelocity.y,
      angularVelocity.z
    );
    
  }
};

/**
 * Get object velocity
 */
export const getObjectVelocity = (objectId) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    return {
      x: body.velocity.x,
      y: body.velocity.y,
      z: body.velocity.z,
    };
  }
  return null;
};

/**
 * Get object position
 */
export const getObjectPosition = (objectId) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    return {
      x: body.position.x,
      y: body.position.y,
      z: body.position.z,
    };
  }
  return null;
};

/**
 * Reset object to initial state
 */
export const resetObjectPhysics = (
  objectId,
  initialPosition,
  initialRotation
) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    const CANNON = require('cannon-es');

    // Reset position
    if (initialPosition) {
      body.position.set(
        initialPosition.x,
        initialPosition.y,
        initialPosition.z
      );
    }

    // Reset rotation
    if (initialRotation) {
      body.quaternion.setFromEuler(
        initialRotation.x,
        initialRotation.y,
        initialRotation.z
      );
    }

    // Reset velocities
    body.velocity.set(0, 0, 0);
    body.angularVelocity.set(0, 0, 0);

    
  }
};

/**
 * Enable/disable physics for object
 */
export const setObjectPhysicsEnabled = (objectId, enabled) => {
  const body = physicsWorld.getPhysicsBody(objectId);
  if (body) {
    body.type = enabled
      ? require('cannon-es').Body.DYNAMIC
      : require('cannon-es').Body.KINEMATIC;
  }
};

/**
 * Get physics world statistics
 */
export const getPhysicsStats = () => {
  const state = store.getState();
  const physicsState = state.physics;

  return {
    enabled: physicsState.previewModePhysics.enabled,
    objectCount: physicsState.previewModePhysics.objects.length,
    totalObjects: physicsState.physicsObjects.length,
    worldGravity: physicsState.worldSettings.gravity,
  };
};

/**
 * Enable debug visuals for physics bodies in a Three.js scene
 */
export const enablePhysicsDebug = (scene) => {
  try {
    physicsWorld.enableDebug(scene);
    
  } catch (e) {
    console.error('Failed to enable physics debug', e);
  }
};

/**
 * Disable physics debug visuals
 */
export const disablePhysicsDebug = () => {
  try {
    physicsWorld.disableDebug();
    
  } catch (e) {
    console.error('Failed to disable physics debug', e);
  }
};

/**
 * Copy saved Physics component settings from a source asset onto its duplicate.
 * Stores under the duplicate's object id so the Physics panel / preview pick it up.
 * @returns {boolean} true when physics was copied
 */
export function copyPhysicsToDuplicate(sourceObject, duplicateObject) {
  if (!sourceObject || !duplicateObject) return false;

  const physicsObjects = store.getState()?.physics?.physicsObjects || [];
  const sourcePhysics = findPhysicsDataForObject(sourceObject, physicsObjects);
  if (!sourcePhysics) return false;

  const newObjectId =
    duplicateObject.name ||
    duplicateObject._id ||
    duplicateObject.url ||
    duplicateObject.temp_file_name ||
    duplicateObject.userData?.temp_file_name;
  if (!newObjectId) return false;

  if (physicsObjects.some((entry) => entry.objectId === newObjectId)) {
    return false;
  }

  let properties;
  try {
    const { objectId: _omit, ...rest } = sourcePhysics;
    properties = JSON.parse(JSON.stringify(rest));
  } catch (_) {
    const { objectId: _omit, ...rest } = sourcePhysics;
    properties = { ...rest };
  }

  store.dispatch(
    setObjectPhysicsProperties({
      objectId: newObjectId,
      properties,
    })
  );
  return true;
}

/**
 * Transform physics data from frontend format to backend format (MongoDB schema)
 * Ensures data structure matches backend expectations
 */
export const transformPhysicsDataForBackend = (physicsObjects) => {
  if (!Array.isArray(physicsObjects)) {
    return [];
  }

  return physicsObjects.map((obj) => {
    // Ensure physicsMaterial has correct structure with name field
    const physicsMaterial = obj.physicsMaterial || {};
    const material = {
      name: physicsMaterial.name || 'Default',
      friction: physicsMaterial.friction ?? 0.5,
      restitution: physicsMaterial.restitution ?? 0.7,
      frictionEquation: physicsMaterial.frictionEquation || 'Default',
    };

    // Build object matching MongoDB schema
    const transformed = {
      objectId: obj.objectId,
      mass: obj.mass ?? 5,
      linearDamping: obj.linearDamping ?? 0.1,
      angularDamping: obj.angularDamping ?? 0.1,
      automaticCenterOfMass: obj.automaticCenterOfMass ?? true,
      centerOfMass: obj.centerOfMass || { x: 0, y: 0, z: 0 }, // Use centerOfMass (not centerOfMassOffset)
      automaticTensor: obj.automaticTensor ?? true,
      inertiaTensor: obj.inertiaTensor || { x: 1, y: 1, z: 1 },
      inertiaTensorRotation: obj.inertiaTensorRotation || { x: 0, y: 0, z: 0 },
      useGravity: obj.useGravity ?? true,
      isKinematic: obj.isKinematic ?? false,
      interpolate: obj.interpolate || 'None',
      collisionDetection: obj.collisionDetection || 'Discrete',
      freezePosition: obj.freezePosition || { x: false, y: false, z: false },
      freezeRotation: obj.freezeRotation || { x: false, y: false, z: false },
      includeLayers: obj.includeLayers || 'Nothing',
      excludeLayers: obj.excludeLayers || 'Nothing',
      colliderType: obj.colliderType || 'Box',
      isViewCollider: obj.isViewCollider ?? true,
      physicsMaterial: material,
      enabled: obj.enabled ?? true,
    };

    return transformed;
  });
};
